import React, { useCallback, useEffect, useState } from 'react';
import { useApiAuth } from '../../hooks/useApiAuth';

const DEFAULT_HERO = {
  title: 'Thoughtfully Handmade, Beautifully Designed',
  subtitle: "Find meaningful, one-of-a-kind pieces perfect for gifting or adding a personal touch to your home.",
  cta_primary: { label: 'Explore Handmade Goods', href: '/portfolio' },
  cta_secondary: { label: 'Shop The Collection', href: '/services' },
  image_url: '',
};
const DEFAULT_HOME_FEATURES = [
  { title: 'Hand Crafted', description: 'Every piece is made by hand with care and attention to detail.' },
  { title: 'Collaborative Design', description: 'We work closely with you to bring your vision to life.' },
  { title: 'Functional Beauty', description: 'Our pieces are crafted to be as functional as they are beautiful — made to be used and enjoyed every day.' },
];
const DEFAULT_HOME_CLOSING = {
  title: "Let's work together to achieve your goals.",
  cta_label: 'Contact Us Today',
  cta_href: '/contact',
};
const DEFAULT_CONTACT = { intro: "Have a question or project in mind? We'd love to hear from you.", email: 'loganccurtis@gmail.com', phone: '(307) 409-2600', form_endpoint: '' };
const DEFAULT_SOCIALS = { linkedin: '', twitter: '', facebook: '', instagram: '' };
const DEFAULT_FOOTER = { tagline: 'Find meaningful, one-of-a-kind pieces perfect for gifting or adding a personal touch to your home.', copyright_name: 'Kalsies Creations' };

export default function SiteManager({ setStatus }) {
  const { authFetch } = useApiAuth();
  const [busy, setBusy] = useState(false);
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [features, setFeatures] = useState(DEFAULT_HOME_FEATURES);
  const [closing, setClosing] = useState(DEFAULT_HOME_CLOSING);
  const [contact, setContact] = useState(DEFAULT_CONTACT);
  const [socials, setSocials] = useState(DEFAULT_SOCIALS);
  const [footer, setFooter] = useState(DEFAULT_FOOTER);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/config');
      setHero({ ...DEFAULT_HERO, ...(data.hero || {}), cta_primary: { ...DEFAULT_HERO.cta_primary, ...((data.hero || {}).cta_primary || {}) }, cta_secondary: { ...DEFAULT_HERO.cta_secondary, ...((data.hero || {}).cta_secondary || {}) } });
      setFeatures(Array.isArray(data.home_features) && data.home_features.length ? data.home_features : DEFAULT_HOME_FEATURES);
      setClosing({ ...DEFAULT_HOME_CLOSING, ...(data.home_closing || {}) });
      setContact({ ...DEFAULT_CONTACT, ...(data.contact || {}) });
      setSocials({ ...DEFAULT_SOCIALS, ...(data.socials || {}) });
      setFooter({ ...DEFAULT_FOOTER, ...(data.footer || {}) });
    } catch (e) {
      setStatus(`Load error: ${e.message}`);
    }
  }, [authFetch, setStatus]);

  useEffect(() => { load(); }, [load]);

  const saveKey = async (key, value) => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key, value } });
      setStatus(`Saved ${key}.`);
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  const uploadHeroImage = async (file) => {
    setBusy(true); setStatus('Uploading…');
    try {
      const up = await authFetch(`/api/admin/upload?filename=${encodeURIComponent(file.name)}`, { method: 'POST', body: file });
      const next = { ...hero, image_url: up.url };
      setHero(next);
      setStatus('Hero image uploaded. Click Save Hero.');
    } catch (e) { setStatus(`Upload error: ${e.message}`); }
    setBusy(false);
  };

  const updFeature = (i, k, v) => {
    const next = features.slice();
    next[i] = { ...next[i], [k]: v };
    setFeatures(next);
  };

  return (
    <div>
      <section className="admin-section">
        <h2>Top of home page (banner)</h2>
        <p className="admin-muted">The big image with the headline and buttons at the top of your home page.</p>
        <div className="admin-form">
          <label>Big headline
            <input type="text" value={hero.title || ''} onChange={(e) => setHero({ ...hero, title: e.target.value })} />
          </label>
          <label>Sentence under the headline
            <textarea rows="2" value={hero.subtitle || ''} onChange={(e) => setHero({ ...hero, subtitle: e.target.value })} />
          </label>
          <label>First button — what it says
            <input type="text" value={hero.cta_primary?.label || ''} onChange={(e) => setHero({ ...hero, cta_primary: { ...(hero.cta_primary || {}), label: e.target.value } })} placeholder="e.g. Explore Handmade Goods" />
          </label>
          <label>First button — where it goes
            <input type="text" value={hero.cta_primary?.href || ''} onChange={(e) => setHero({ ...hero, cta_primary: { ...(hero.cta_primary || {}), href: e.target.value } })} placeholder="e.g. /portfolio" />
          </label>
          <label>Second button — what it says
            <input type="text" value={hero.cta_secondary?.label || ''} onChange={(e) => setHero({ ...hero, cta_secondary: { ...(hero.cta_secondary || {}), label: e.target.value } })} placeholder="e.g. Shop The Collection" />
          </label>
          <label>Second button — where it goes
            <input type="text" value={hero.cta_secondary?.href || ''} onChange={(e) => setHero({ ...hero, cta_secondary: { ...(hero.cta_secondary || {}), href: e.target.value } })} placeholder="e.g. /services" />
          </label>
          <label>Banner image (leave the address alone unless you know it)
            <input type="text" value={hero.image_url || ''} onChange={(e) => setHero({ ...hero, image_url: e.target.value })} placeholder="Leave empty to keep the current banner" />
          </label>
          <label>Or upload a new banner photo
            <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadHeroImage(f); }} />
          </label>
          {hero.image_url && <img src={hero.image_url} alt="banner preview" style={{ maxWidth: 240, borderRadius: 8 }} />}
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveKey('hero', hero)}>Save banner</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>"Why Choose Us?" boxes</h2>
        <p className="admin-muted">The three feature boxes on the home page under the banner.</p>
        <div className="admin-form">
          {features.map((f, i) => (
            <div key={i} className="admin-card">
              <label>Box heading
                <input type="text" value={f.title || ''} onChange={(e) => updFeature(i, 'title', e.target.value)} placeholder="e.g. Hand Crafted" />
              </label>
              <label>Box description
                <textarea rows="2" value={f.description || ''} onChange={(e) => updFeature(i, 'description', e.target.value)} />
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="admin-btn" onClick={() => setFeatures(features.filter((_, j) => j !== i))}>Remove this box</button>
              </div>
            </div>
          ))}
          <button className="admin-btn" onClick={() => setFeatures([...features, { title: '', description: '' }])}>+ Add another box</button>
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveKey('home_features', features)}>Save boxes</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Invitation at the bottom of the home page</h2>
        <p className="admin-muted">The last section of your home page — currently "Let's work together to achieve your goals." with the "Contact Us Today" button.</p>
        <div className="admin-form">
          <label>Invitation line
            <input type="text" value={closing.title || ''} onChange={(e) => setClosing({ ...closing, title: e.target.value })} placeholder="e.g. Let's work together to achieve your goals." />
          </label>
          <label>Button text
            <input type="text" value={closing.cta_label || ''} onChange={(e) => setClosing({ ...closing, cta_label: e.target.value })} placeholder="e.g. Contact Us Today" />
          </label>
          <label>Button link
            <input type="text" value={closing.cta_href || ''} onChange={(e) => setClosing({ ...closing, cta_href: e.target.value })} placeholder="e.g. /contact" />
          </label>
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveKey('home_closing', closing)}>Save invitation</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Contact information</h2>
        <p className="admin-muted">Shown on the Contact page and in the footer.</p>
        <div className="admin-form">
          <label>Intro sentence on the Contact page
            <textarea rows="2" value={contact.intro || ''} onChange={(e) => setContact({ ...contact, intro: e.target.value })} />
          </label>
          <label>Email address
            <input type="email" value={contact.email || ''} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          </label>
          <label>Phone number
            <input type="text" value={contact.phone || ''} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          </label>
          <label>Contact form submissions go to (paste your Formspree URL — otherwise the form just shows a thank you)
            <input type="text" value={contact.form_endpoint || ''} onChange={(e) => setContact({ ...contact, form_endpoint: e.target.value })} placeholder="https://formspree.io/f/abcdefg" />
          </label>
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveKey('contact', contact)}>Save contact info</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Social media links</h2>
        <p className="admin-muted">Full addresses (starting with https://). Leave a box empty and that link won't show anywhere.</p>
        <div className="admin-form">
          {['linkedin', 'twitter', 'facebook', 'instagram'].map((k) => (
            <label key={k}>{k[0].toUpperCase() + k.slice(1)} address
              <input type="text" value={socials[k] || ''} onChange={(e) => setSocials({ ...socials, [k]: e.target.value })} placeholder={`https://www.${k}.com/…`} />
            </label>
          ))}
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveKey('socials', socials)}>Save social links</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Site footer</h2>
        <p className="admin-muted">The bar at the very bottom of every page.</p>
        <div className="admin-form">
          <label>Short description in the footer
            <textarea rows="2" value={footer.tagline || ''} onChange={(e) => setFooter({ ...footer, tagline: e.target.value })} />
          </label>
          <label>Business name for the copyright line
            <input type="text" value={footer.copyright_name || ''} onChange={(e) => setFooter({ ...footer, copyright_name: e.target.value })} placeholder="e.g. Kalsies Creations" />
          </label>
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveKey('footer', footer)}>Save footer</button>
        </div>
      </section>
    </div>
  );
}
